import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import type { User as SupabaseUser, Session } from '@supabase/supabase-js';
import type { User } from '@sharecost/shared';
import { supabase, uniqueChannelTopic } from '@/lib/supabase';
import { queryClient } from '@/lib/query-client';
import { authEvents, type InvalidSessionReason } from '@/lib/auth-events';
import { Analytics } from '@/services/analytics.service';
import { BuhService } from '@/services/buh.service';

interface AuthState {
  user: SupabaseUser | null;
  profile: User | null;
  session: Session | null;
  isLoading: boolean;
}

interface AuthContextValue extends AuthState {
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string, name: string) => Promise<void>;
  signInWithMagicLink: (email: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  forceSignOut: (reason: InvalidSessionReason) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    profile: null,
    session: null,
    isLoading: true,
  });

  const initializedRef = useRef(false);
  const forceSignOutInFlightRef = useRef(false);
  // Track whether the current SIGNED_IN event is a real login (not a page refresh).
  // sessionStorage survives OAuth/magic-link redirects but clears on tab close.
  const pendingSignInRef = useRef<string | null>(
    sessionStorage.getItem('pendingSignIn')
  );

  /**
   * Resolve BUH ID via Edge Function.
   * Returns buh_id if found, null otherwise. Never throws.
   */
  const resolveBuhId = useCallback(async (profile: User): Promise<string | null> => {
    try {
      if (!profile.email) return null;
      return await BuhService.lookupBuhId(profile.email);
    } catch {
      return null;
    }
  }, []);

  // Safe variant of signOut() — swallows errors so module-level handlers
  // (query cache, realtime channel callbacks) don't need try/catch wrappers.
  // The in-flight ref dedupes concurrent triggers; it is reset by the
  // SIGNED_OUT branch of onAuthStateChange, not here, so late-arriving
  // events (e.g. DELETE realtime event arriving after signOut resolves)
  // don't re-run tracking, query cache clears, or another signOut call.
  const forceSignOut = useCallback(async (reason: InvalidSessionReason) => {
    if (forceSignOutInFlightRef.current) return;
    forceSignOutInFlightRef.current = true;
    try {
      Analytics.track({ name: 'Forced Logout', properties: { reason } });
      Analytics.reset();
      queryClient.clear();
      await supabase.auth.signOut({ scope: 'local' });
    } catch (e) {
      forceSignOutInFlightRef.current = false;
      console.error('forceSignOut failed', e);
    }
  }, []);

  const fetchProfile = useCallback(async (userId: string): Promise<User | null> => {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) {
      // PGRST116 = "0 rows". RLS allows reading our own row, so no row means
      // the account was hard-deleted (e.g. from another device). Force logout.
      if (error.code === 'PGRST116') {
        void forceSignOut('account_deleted');
        return null;
      }
      console.error('Failed to fetch user profile:', error);
      return null;
    }
    return data as User;
  }, [forceSignOut]);

  const refreshProfile = useCallback(async () => {
    if (!state.user) return;
    const profile = await fetchProfile(state.user.id);
    setState(prev => ({ ...prev, profile }));
  }, [state.user, fetchProfile]);

  useEffect(() => {
    let isMounted = true;

    // Safety net — guarantee spinner never runs forever
    const safetyTimeout = setTimeout(() => {
      if (isMounted) {
        setState(prev => {
          if (prev.isLoading) {
            console.warn('Auth initialization timed out after 10s');
            return { ...prev, isLoading: false };
          }
          return prev;
        });
      }
    }, 10_000);

    // Sole initializer — INITIAL_SESSION fires on mount, replacing getSession()
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!isMounted) return;

        try {
          // TOKEN_REFRESHED: update session/user, skip redundant profile fetch
          if (event === 'TOKEN_REFRESHED' && initializedRef.current) {
            setState(prev => ({
              ...prev,
              user: session?.user ?? null,
              session: session ?? null,
            }));
            return;
          }

          if (session?.user) {
            // consumePendingSignIn returns the method only if a signIn was explicitly called
            const signInMethod = event === 'SIGNED_IN' ? consumePendingSignIn() : null;

            // Set session immediately (unblocks auth), fetch profile in background
            setState(prev => ({
              ...prev,
              user: session.user,
              session,
              isLoading: false,
            }));
            initializedRef.current = true;

            // Profile fetch is non-blocking — UI shows without it
            fetchProfile(session.user.id).then(async (profile) => {
              if (!isMounted) return;
              setState(prev => ({ ...prev, profile }));

              if (profile) {
                // Resolve final analytics ID — await BUH lookup if not yet known
                let analyticsId = profile.buh_id || profile.id;
                if (!profile.buh_id) {
                  const buhId = await resolveBuhId(profile);
                  if (!isMounted) return; // component unmounted during BUH lookup
                  if (buhId) analyticsId = buhId;
                }

                // Shared Mixpanel properties — written via set_once() to avoid
                // overwriting values the Wallet app set on the same buh_id profile.
                const sharedProps: Record<string, any> = {
                  $email: profile.email,
                  $name: profile.display_name,
                };
                // ShareCost-specific properties — namespaced under sc_*, written via set().
                const scProps: Record<string, any> = {
                  sc_display_name: profile.display_name,
                  sc_default_currency: profile.default_currency,
                  sc_timezone: profile.timezone,
                  sc_signup_at: profile.created_at,
                  sc_subscription_tier: profile.subscription_tier,
                };
                if (analyticsId !== profile.id) {
                  scProps.sharecost_user_id = profile.id;
                }
                Analytics.identify(analyticsId, sharedProps, scProps);
              }

              if (signInMethod) {
                Analytics.track({ name: 'Login Successful', properties: { method: signInMethod as 'email' | 'google' | 'magic_link' } });
              }
            });
          } else {
            setState({ user: null, session: null, profile: null, isLoading: false });
            // Supabase confirmed sign-out — allow a fresh forceSignOut cycle
            // if the user signs back in and later gets invalidated again.
            forceSignOutInFlightRef.current = false;
          }
          initializedRef.current = true;
        } catch (err) {
          if (!isMounted) return;
          console.error('Auth state change error:', err);
          // Resolve loading even on error — app without profile > infinite spinner
          setState(prev => ({
            ...prev,
            user: session?.user ?? null,
            session: session ?? null,
            isLoading: false,
          }));
          initializedRef.current = true;
        }
      }
    );

    return () => {
      isMounted = false;
      clearTimeout(safetyTimeout);
      subscription.unsubscribe();
    };
  }, [fetchProfile]);

  // Bridge module-level signals (query-client error handler, Realtime channel
  // errors) to the in-context forceSignOut. Registered once per AuthProvider
  // lifetime; cleared on unmount so tests / HMR don't leak stale handlers.
  useEffect(() => {
    authEvents.setHandler((reason) => {
      void forceSignOut(reason);
    });
    return () => {
      authEvents.setHandler(null);
    };
  }, [forceSignOut]);

  // Watch our own `public.users` row for DELETE events via Realtime. When the
  // account is hard-deleted from another device, this fires immediately even
  // when the tab is active (window-focus refetch does not run, and the access
  // token is still valid for up to ~1h so REST calls keep succeeding until
  // the token expires). The DELETE event is visible to us because the
  // `u_select` RLS policy allows `id = auth.uid()` on the OLD record.
  useEffect(() => {
    const userId = state.user?.id;
    if (!userId) return;

    const channel = supabase
      .channel(uniqueChannelTopic(`session-watch-${userId}`))
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'users', filter: `id=eq.${userId}` },
        () => {
          void forceSignOut('account_deleted');
        }
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [state.user?.id, forceSignOut]);

  const setPendingSignIn = (method: string) => {
    pendingSignInRef.current = method;
    sessionStorage.setItem('pendingSignIn', method);
  };

  const consumePendingSignIn = (): string | null => {
    const method = pendingSignInRef.current;
    pendingSignInRef.current = null;
    sessionStorage.removeItem('pendingSignIn');
    return method;
  };

  const signInWithEmail = async (email: string, password: string) => {
    setPendingSignIn('email');
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) { consumePendingSignIn(); throw error; }
  };

  const signUpWithEmail = async (email: string, password: string, name: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { display_name: name },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;

    // Supabase returns a user with empty identities when the email already exists
    // (instead of throwing an error, to prevent email enumeration)
    if (data.user && data.user.identities?.length === 0) {
      throw new Error('An account with this email already exists. Please sign in instead.');
    }
  };

  const signInWithMagicLink = async (email: string) => {
    setPendingSignIn('magic_link');
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) { consumePendingSignIn(); throw error; }
  };

  const signInWithGoogle = async () => {
    setPendingSignIn('google');
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) { consumePendingSignIn(); throw error; }
  };

  const signOut = async () => {
    Analytics.track({ name: 'Logout' });
    Analytics.reset();
    queryClient.clear();
    const { error } = await supabase.auth.signOut({ scope: 'local' });
    if (error) throw error;
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/callback`,
    });
    if (error) throw error;
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        signInWithEmail,
        signUpWithEmail,
        signInWithMagicLink,
        signInWithGoogle,
        signOut,
        forceSignOut,
        resetPassword,
        updatePassword,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
